import {Fragment, useState, useCallback, useEffect} from 'react';
import {Configure, Index, useStats} from 'react-instantsearch';
import {useSearchParam} from 'react-use';
import {Tab} from '@headlessui/react';
import {Pagination} from './Pagination';
import {ProductHits} from './ProductHits';
import {BlogHits} from './BlogHits';
import {SkiTestHits} from './SkiTestHits';
import {ProductFilter, PRODUCT_FILTERS} from './ProductFilter';

function CustomStats() {
  const {nbHits} = useStats();
  return <span>({nbHits})</span>;
}

const StyledTab = ({
  children,
  indexName,
  configureProps,
}: {
  children?: React.ReactNode;
  indexName: string;
  configureProps: {
    query: string;
    filters?: string;
  };
}) => (
  <Tab as={Fragment}>
    {({selected}) => (
      <button
        className={`outline-none text-22/30 md:text-32/44 font-extrabold font-header flex gap-1 ${
          selected ? 'underline' : ''
        }`}
      >
        <Index indexName={indexName}>
          {children}
          <Configure {...configureProps} />
          <CustomStats />
        </Index>
      </button>
    )}
  </Tab>
);

const stockPolicyFilter =
  'NOT named_tags.pfs:"hidden:hide" AND (inventory_available:true OR tags:"Bundle Product")';

const INDEXED_TABS: {
  [key: string]: number;
  products: number;
  blog: number;
  skitest: number;
} = {
  products: 0,
  blog: 1,
  skitest: 2,
};

const reversedTabs = Object.fromEntries(
  Object.entries(INDEXED_TABS).map(([key, value]) => [value, key]),
);

export const SearchPage = ({searchTerm}: {searchTerm: string}) => {
  const getTabQuery = useSearchParam('tab');
  const tabQuery = getTabQuery || 'products';

  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(INDEXED_TABS[tabQuery]);
  }, [tabQuery]);

  const handleSelectedIndex = useCallback((indexTab: number) => {
    setSelectedIndex(indexTab);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('tab', reversedTabs[indexTab] ?? reversedTabs[0]);
    window.history.replaceState(null, '', `?${searchParams.toString()}`);
  }, []);

  return (
    <Tab.Group selectedIndex={selectedIndex} onChange={handleSelectedIndex}>
      <div className="mt-[60px] md:mt-[99px]">
        <Tab.List className="flex gap-6">
          <StyledTab
            indexName="shopify_products"
            configureProps={{
              query: searchTerm,
              filters: stockPolicyFilter,
            }}
          >
            Products
          </StyledTab>
          <StyledTab
            indexName="sanity_skiTest"
            configureProps={{
              query: searchTerm,
            }}
          >
            Ski Test
          </StyledTab>
          <StyledTab
            indexName="sanity_blog"
            configureProps={{
              query: searchTerm,
            }}
          >
            Chairlift Chat
          </StyledTab>
        </Tab.List>
      </div>
      <Tab.Panels>
        <Tab.Panel>
          <Index indexName="shopify_products">
            <Configure query={searchTerm} filters={stockPolicyFilter} />
            <div className="flex mt-5 md:mt-10">
              <div className="hidden xl:block w-[262px] shrink-0 transition-all">
                {PRODUCT_FILTERS?.map((item) => (
                  <ProductFilter key={item.filter.name} {...item} />
                ))}
              </div>
              <div className="w-full transition-all xl:ml-[131px]">
                <ProductHits />
              </div>
            </div>
            <div className="mt-4">
              <Pagination />
            </div>
          </Index>
        </Tab.Panel>
        <Tab.Panel>
          <Index indexName="sanity_skiTest">
            <Configure query={searchTerm} />
            <SkiTestHits />
            <div className="mt-4">
              <Pagination />
            </div>
          </Index>
        </Tab.Panel>
        <Tab.Panel>
          <Index indexName="sanity_blog">
            <Configure query={searchTerm} />
            <BlogHits />
            <div className="mt-4">
              <Pagination />
            </div>
          </Index>
        </Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  );
};
